import { Rise } from "@/components/humble/AnimatedText";
import ChapterMark from "@/components/humble/ChapterMark";
import Painting from "@/components/humble/Painting";
import { HUMBLE } from "@/constants/testIds";

const CAPS = [
  {
    key: "every-animal",
    n: "01",
    title: "Every animal, kept.",
    lede: "A quiet page for each one on the place.",
    bullets: [
      "Name, age, lineage, weights, photos.",
      "The good stories — and the hard ones.",
      "One tap to see who\u2019s who without hunting a notebook.",
    ],
    art: "/art/goats-and-heifer.png",
    alt: "A painted heifer and goats standing in a warm sage meadow.",
  },
  {
    key: "health-care",
    n: "02",
    title: "Health & care.",
    lede: "Logged at the fence, remembered forever.",
    bullets: [
      "Vaccines, wormings, hoof trims, treatments.",
      "Gentle reminders for what\u2019s due.",
      "A clear history the vet can actually read.",
    ],
    art: "/art/hands-at-the-fence.png",
    alt: "A painted hand feeding chickens at a wooden fence at first light.",
  },
  {
    key: "breeding",
    n: "03",
    title: "Breeding.",
    lede: "So the season never surprises you.",
    bullets: [
      "Pairings and due dates you can trust.",
      "Lambings, kiddings, hatchings.",
      "Candling notes and hatch outcomes, in one place.",
    ],
    art: "/art/mother-hen-chicks.png",
    alt: "A painted mother hen sheltering yellow chicks in warm lamplight.",
  },
  {
    key: "daily-chores",
    n: "04",
    title: "Daily chores.",
    lede: "Morning and evening, the whole family can check off together.",
    bullets: [
      "Simple shared routines — not another app to fight.",
      "Kids can mark their own list before school.",
      "You\u2019ll know before dark what still needs doing.",
    ],
    art: "/art/kids-morning-chores.png",
    alt: "Two painted children walking to the barn at sunrise, one holding an egg basket.",
  },
];

export default function Capabilities() {
  return (
    <section
      id="what-it-does"
      data-testid={HUMBLE.capabilitiesSection}
      className="relative isolate overflow-hidden bg-cream text-earth"
    >
      <ChapterMark numeral="III" label="What it does" />

      <div className="relative z-[1] mx-auto w-full max-w-[1400px] px-6 py-28 md:px-12 md:py-40">
        <Rise>
          <div className="mb-4 flex items-center gap-3 font-body uppercase text-[11px] tracking-[0.42em] text-earth/60">
            <span className="inline-block h-px w-8 bg-earth/40" />
            Chapter III
          </div>
        </Rise>
        <Rise delay={0.05}>
          <h2 className="max-w-3xl font-display text-4xl leading-[1.05] tracking-tight md:text-5xl lg:text-[60px]">
            Not a farm ERP. Not a spreadsheet.
            <br />
            <span className="italic text-earth/70">One gentle place that holds it all.</span>
          </h2>
        </Rise>

        <div className="mt-16 grid grid-cols-1 gap-x-10 gap-y-16 md:grid-cols-2 md:gap-y-24">
          {CAPS.map((c, i) => (
            <Rise
              key={c.key}
              delay={0.05 + i * 0.06}
              className={i % 2 === 1 ? "md:mt-16" : ""}
            >
              <article
                data-testid={HUMBLE.capability(c.key)}
                className="group relative"
              >
                <div className="overflow-hidden rounded-[6px]">
                  <Painting
                    src={c.art}
                    alt={c.alt}
                    wrapperClassName="aspect-[4/3]"
                  />
                </div>
                <div className="mt-6 flex items-baseline gap-4">
                  <span className="font-display italic text-2xl text-gold-deep">
                    {c.n}
                  </span>
                  <h3 className="font-display text-2xl leading-tight tracking-tight md:text-[30px]">
                    {c.title}
                  </h3>
                </div>
                <p className="mt-2 font-body text-[15px] italic text-earth/70">
                  {c.lede}
                </p>
                <ul className="mt-4 space-y-2 font-body text-[15px] text-earth/85">
                  {c.bullets.map((b, j) => (
                    <li key={j} className="flex items-start gap-3">
                      <span
                        aria-hidden
                        className="mt-[10px] inline-block h-[6px] w-[6px] rounded-full bg-gold"
                      />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </article>
            </Rise>
          ))}
        </div>
      </div>
    </section>
  );
}
